import { CheckOutlined, PlusOutlined } from "@ant-design/icons"
import { Tooltip } from "antd"
import React, { useRef, useState } from "react"

import { EVENT_COLOR_PALETTE } from "../../helpers/event-custom-colors.ts"

interface CalendarColorPickerProps {
  // The chosen accent hex, or null/undefined to leave the event on its
  // type default — this control never forces a choice.
  value?: string | null
  onChange?: (hex: string | null) => void
}

const SWATCH_CLASS =
  "size-7 rounded-full border border-black/10 cursor-pointer flex items-center justify-center shrink-0 transition-transform hover:scale-110"

export function CalendarColorPicker({
  value,
  onChange
}: CalendarColorPickerProps) {
  const nativeInputRef = useRef<HTMLInputElement>(null)
  // A custom pick outside the palette still needs its own swatch to show as
  // selected — otherwise choosing "+" would leave nothing looking picked.
  const [customHex, setCustomHex] = useState<string | null>(
    value && !EVENT_COLOR_PALETTE.some((c) => c.hex === value) ? value : null
  )

  const isSelected = (hex: string) => value?.toLowerCase() === hex.toLowerCase()

  return (
    <div className={"flex flex-wrap gap-2 items-center"}>
      {EVENT_COLOR_PALETTE.map(({ key, label, hex }) => (
        <Tooltip key={key} title={label}>
          <button
            type={"button"}
            aria-label={label}
            aria-pressed={isSelected(hex)}
            className={SWATCH_CLASS}
            style={{
              backgroundColor: hex,
              boxShadow: isSelected(hex)
                ? `0 0 0 2px white, 0 0 0 4px ${hex}`
                : undefined
            }}
            onClick={() => onChange?.(hex)}
          >
            {isSelected(hex) && (
              <CheckOutlined className={"text-white text-xs"} />
            )}
          </button>
        </Tooltip>
      ))}

      {customHex && (
        <Tooltip title={"Custom color"}>
          <button
            type={"button"}
            aria-label={"Custom color"}
            aria-pressed={isSelected(customHex)}
            className={SWATCH_CLASS}
            style={{
              backgroundColor: customHex,
              boxShadow: isSelected(customHex)
                ? `0 0 0 2px white, 0 0 0 4px ${customHex}`
                : undefined
            }}
            onClick={() => onChange?.(customHex)}
          >
            {isSelected(customHex) && (
              <CheckOutlined className={"text-white text-xs"} />
            )}
          </button>
        </Tooltip>
      )}

      <Tooltip title={"Pick a custom color"}>
        <button
          type={"button"}
          aria-label={"Pick a custom color"}
          className={`${SWATCH_CLASS} border-dashed border-gray-300 text-gray-400 bg-white`}
          onClick={() => nativeInputRef.current?.click()}
        >
          <PlusOutlined className={"text-xs"} />
        </button>
      </Tooltip>

      {/* The browser's own color picker — reliable across platforms without
          building and maintaining a custom hue/saturation UI. */}
      <input
        ref={nativeInputRef}
        type={"color"}
        className={"sr-only"}
        aria-hidden
        tabIndex={-1}
        value={customHex || "#3B82F6"}
        onChange={(e) => {
          const hex = e.target.value
          setCustomHex(hex)
          onChange?.(hex)
        }}
      />
    </div>
  )
}
